import { useEffect, useRef } from "react";
import { type To } from "react-router";

import { HeaderBackButton } from "./components/header-buttons/header-back-button";

interface PageHeaderProps {
	backButtonPath?: To;
	buttons?: React.ReactNode;
	children?: React.ReactNode;
	description?: React.ReactNode | string;
	dropdown?: React.ReactNode;
	includeBackButton?: boolean;
	isLoading?: boolean;
	sticky?: boolean;
	tabs?: React.ReactNode;
	title: React.ReactNode | string;
}

export const PageHeader = ({
	backButtonPath,
	buttons,
	children,
	description,
	dropdown,
	includeBackButton = false,
	isLoading = false,
	title,
}: PageHeaderProps) => {
	const headerRef = useRef<HTMLDivElement>(null);
	const showBackButton =
		!isLoading && (includeBackButton || backButtonPath !== undefined);

	useEffect(() => {
		const headerElement = headerRef.current;
		if (!headerElement) return;

		const updateHeight = () => {
			const height = headerElement.offsetHeight;

			// On mobile, add the site header height
			const isMobile =
				window.matchMedia?.("(max-width: 1023px)")?.matches ?? false;
			let siteHeaderHeight: number = 0;

			if (isMobile) {
				const siteHeader = document.querySelector("#mobile-header");
				if (siteHeader) {
					siteHeaderHeight = (siteHeader as HTMLElement).offsetHeight;
				}
			}

			const totalHeight = height + siteHeaderHeight;
			document.documentElement.style.setProperty(
				"--page-header-height",
				`${totalHeight}px`,
			);
		};

		// Initial height calculation - use requestAnimationFrame to ensure DOM is fully laid out
		requestAnimationFrame(() => {
			updateHeight();
		});

		// Use ResizeObserver to watch for size changes (handles content changes, font loading, etc.)
		const resizeObserver = new ResizeObserver(() => {
			updateHeight();
		});

		resizeObserver.observe(headerElement);

		// Also observe the site header on mobile if it exists
		const siteHeader = document.querySelector("#mobile-header");
		if (siteHeader) {
			resizeObserver.observe(siteHeader);
		}

		// Also listen for window resize events (for cases where layout changes affect header)
		window.addEventListener("resize", updateHeight);

		// Cleanup
		return () => {
			resizeObserver.disconnect();
			window.removeEventListener("resize", updateHeight);
		};
	}, [
		title,
		description,
		children,
		buttons,
		dropdown,
		backButtonPath,
		includeBackButton,
		isLoading,
	]);

	// Keep rendering when there are children: TabsLayout's tab list lives here
	if (!title && !description && !children) return null;

	return (
		<div
			className="top-0 z-20 border-b border-border-secondary bg-primary px-4 py-4 text-primary shadow-header lg:sticky lg:px-12 lg:py-6"
			ref={headerRef}
		>
			<div className="flex flex-col justify-between gap-2 md:flex-row">
				<div className="flex w-full items-center gap-1">
					{showBackButton && <HeaderBackButton path={backButtonPath} />}

					<div className="grow">
						{typeof title === "string" ? (
							<h1 className="text-lg font-semibold lg:text-xl xl:text-2xl/8">
								{title}
							</h1>
						) : (
							title
						)}
						{typeof description === "string" ? (
							<p className="text-sm text-gray-500 xl:text-md/7">
								{description}
							</p>
						) : (
							description
						)}
					</div>
				</div>

				{!isLoading && (buttons || dropdown) && (
					<div className="flex items-start">
						<div className="flex items-center gap-2">
							{buttons ?? buttons}

							{dropdown ?? dropdown}
						</div>
					</div>
				)}
			</div>

			{children && <div className="flex items-start">{children}</div>}
		</div>
	);
};
